import { useEffect, useState } from "react";
import { CheckCircle2, Loader2, Send, AlertCircle, X, UserCheck } from "lucide-react";
import { useLang } from "../../i18n/LangContext";
import { api, apiErrorField } from "../../lib/api";
import { validate } from "../../lib/validate";
import { Section, Field, inputProps, OptionList } from "./Primitives";
import { ContactLine } from "./ContactLine";

const EMPTY = { company: "", country: "", vertical: "", need: "", urgency: "", contact: "", email: "", phone: "", description: "", whatsapp_optin: false };
const RULES = {
  company: ["required", ["min", 2]], country: ["required", ["min", 2]], vertical: ["required"], need: ["required"],
  urgency: ["required"], contact: ["required", ["min", 2]], email: ["required", "email"], phone: ["phone"],
  description: ["required", ["min", 10]],
};

export const preferExpert = (expert) => {
  window.dispatchEvent(new CustomEvent("indushub:prefer-expert", { detail: { id: expert.id, name: expert.name, vertical: expert.verticals[0] } }));
  document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
};

export const openProTab = (tab, extra = {}) => {
  window.dispatchEvent(new CustomEvent("indushub:tab", { detail: { tab, ...extra } }));
  document.getElementById("espace")?.scrollIntoView({ behavior: "smooth" });
};

const Confirmation = ({ result, onReset }) => {
  const { t } = useLang();
  const parts = t.contact.confirm(
    { need: t.needs[result.need], company: result.company, country: result.country, email: result.email },
    t.verticals[result.vertical]
  );
  return (
    <div className="f-confirm" data-testid="diagnostic-confirmation" role="status">
      <div className="success-msg" style={{ marginTop: 0 }}>
        <CheckCircle2 aria-hidden="true" />
        <span>Ticket <b data-testid="diagnostic-confirmation-ref">{result.ref}</b> {t.contact.created}</span>
      </div>
      <p className="f-confirm-text">{parts.map((p, i) => (i % 2 ? <b key={i}>{p}</b> : <span key={i}>{p}</span>))}</p>
      <button type="button" className="btn btn-ghost" onClick={onReset} data-testid="diagnostic-new-request-btn">{t.contact.reset}</button>
    </div>
  );
};

export const DiagnosticForm = () => {
  const { t, lang } = useLang();
  const c = t.contact;
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [result, setResult] = useState(null);
  const [preferred, setPreferred] = useState(null);

  useEffect(() => {
    const onPrefer = (e) => {
      setPreferred(e.detail);
      setResult(null);
      setValues((v) => ({ ...v, vertical: v.vertical || e.detail.vertical }));
    };
    window.addEventListener("indushub:prefer-expert", onPrefer);
    return () => window.removeEventListener("indushub:prefer-expert", onPrefer);
  }, []);

  const set = (k) => (e) => {
    setValues((v) => ({ ...v, [k]: e.target.value }));
    if (errors[k]) setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(values, { ...RULES, phone: values.whatsapp_optin ? ["required", "intl"] : ["phone"] });
    setErrors(errs);
    if (Object.keys(errs).length) {
      document.getElementById(`diag-${Object.keys(errs)[0]}`)?.focus();
      return;
    }
    setStatus("sending");
    try {
      const phone = values.whatsapp_optin ? values.phone.replace(/[\s().-]/g, "") : values.phone || null;
      const { data } = await api.post("/diagnostics", { ...values, phone, lang, preferred_expert_id: preferred?.id || null });
      setResult(data);
      setStatus("idle");
      setValues(EMPTY);
      setPreferred(null);
    } catch (err) {
      setErrors(apiErrorField(err));
      setStatus("error");
    }
  };

  const f = (k, required = true) => ({ ...inputProps(`diag-${k}`, errors[k], required), value: values[k], onChange: set(k) });

  return (
    <Section id="contact" eyebrow={c.eyebrow} title={c.title} desc={c.desc} testid="contact-section">
      <div className="panel rivets-bottom form-panel" data-testid="diagnostic-panel">
        <span className="rv" aria-hidden="true" />
        <div className="console-head">
          <span className="console-title">{c.ticket}</span>
          <span className="live-tag"><span className="live-dot" />{c.secure}</span>
        </div>
        {result ? <Confirmation result={result} onReset={() => setResult(null)} /> : (
          <form onSubmit={submit} noValidate data-testid="diagnostic-form">
            {preferred && (
              <div className="preferred-chip" data-testid="diag-preferred-expert">
                <UserCheck aria-hidden="true" /><span>{t.matchingX.preferred} : <b>{preferred.name}</b></span>
                <button type="button" className="link-btn" onClick={() => setPreferred(null)} aria-label={t.matchingX.clear} data-testid="diag-preferred-clear-btn"><X size={14} aria-hidden="true" /></button>
              </div>
            )}
            <div className="f-grid" style={{ marginTop: 8 }}>
              <Field id="diag-company" label={c.company} error={errors.company} required><input type="text" placeholder={c.companyPh} autoComplete="organization" {...f("company")} /></Field>
              <Field id="diag-country" label={c.country} error={errors.country} required><input type="text" placeholder={c.countryPh} autoComplete="country-name" {...f("country")} /></Field>
              <Field id="diag-vertical" label={c.vertical} error={errors.vertical} required><select {...f("vertical")}><OptionList map={t.verticals} withEmpty /></select></Field>
              <Field id="diag-need" label={c.need} error={errors.need} required><select {...f("need")}><OptionList map={t.needs} withEmpty /></select></Field>
              <Field id="diag-urgency" label={c.urgency} error={errors.urgency} required><select {...f("urgency")}><OptionList map={t.urgencies} withEmpty /></select></Field>
              <Field id="diag-contact" label={c.contact} error={errors.contact} required><input type="text" placeholder={c.contactPh} autoComplete="name" {...f("contact")} /></Field>
              <Field id="diag-email" label={c.email} error={errors.email} required><input type="email" placeholder={c.emailPh} autoComplete="email" {...f("email")} /></Field>
              <Field id="diag-phone" label={c.phone} error={errors.phone}><input type="tel" placeholder={c.phonePh} autoComplete="tel" {...f("phone", false)} /></Field>
              <Field id="diag-description" label={c.desc2} error={errors.description} span2 required><textarea rows={5} placeholder={c.descPh} {...f("description")} /></Field>
              <div className="f-field f-span2">
                <label className="check-row" htmlFor="diag-optin">
                  <input type="checkbox" id="diag-optin" checked={values.whatsapp_optin} onChange={(e) => setValues((v) => ({ ...v, whatsapp_optin: e.target.checked }))} data-testid="diag-optin-checkbox" />
                  <span>{t.contactX.optin} <small style={{ color: "var(--text-2)" }}>— {t.contactX.phoneIntl}</small></span>
                </label>
              </div>
            </div>
            {status === "error" && <p className="error-banner" role="alert" data-testid="diagnostic-error"><AlertCircle aria-hidden="true" />{Object.keys(errors).length ? t.errors.fixErrors : t.errors.generic}</p>}
            <div className="f-actions">
              <span className="f-note">IH-DIAG-<b>————</b> · {c.note}</span>
              <button type="submit" className="btn btn-primary" disabled={status === "sending"} data-testid="diagnostic-submit-btn">
                {status === "sending" ? <><Loader2 className="spin" aria-hidden="true" />{c.sending}</> : <><Send aria-hidden="true" />{c.submit}</>}
              </button>
            </div>
          </form>
        )}
      </div>
      <div className="contact-links">
        <span>{c.expertQ} <button type="button" className="link-btn" onClick={() => openProTab("expert")} data-testid="contact-join-expert-link">{c.expertLink} →</button></span>
        <span>{c.companyQ} <button type="button" className="link-btn" onClick={() => openProTab("client")} data-testid="contact-client-space-link">{c.companyLink} →</button></span>
      </div>
      <ContactLine testid="contact-section-info" />
    </Section>
  );
};
